import data from "@/data/inventory.json";

// Bundled AutoTrader snapshot, used only if the live stock API can't be reached
export const SNAPSHOT_DATE = "22 October 2026";
export let vehicles = Array.isArray(data?.vehicles) ? data.vehicles : [];
export let lastSynced = null;
export let isLive = false;

const gbpFmt = new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 });
export const gbp = (n) => gbpFmt.format(n ?? 0);
export const miles = (n) => `${(n ?? 0).toLocaleString("en-GB")} miles`;

export const vehicleName = (v) => `${v.make} ${v.model}`;
export const slugOf = (v) => `${v.year}-${v.make}-${v.model}-${v.id}`.toLowerCase().replace(/[^a-z0-9]+/g, "-");
export const vehiclePath = (v) => `/used-cars/${slugOf(v)}`;
export const findById = (id) => vehicles.find((v) => v.id === id);
export const findBySlug = (slug) => findById((slug || "").split("-").pop());

const uniq = (arr) => [...new Set(arr.filter(Boolean))];
const derive = (list) => ({
  makes: uniq(list.map((v) => v.make)).sort(),
  fuels: uniq(list.map((v) => v.fuel)).sort(),
  transmissions: uniq(list.map((v) => v.transmission)).sort(),
  bodyTypes: uniq(list.map((v) => v.bodyType)).sort(),
});
export let { makes, fuels, transmissions, bodyTypes } = derive(vehicles);

export const setInventory = (list, synced) => {
  vehicles = list;
  lastSynced = synced || null;
  isLive = true;
  ({ makes, fuels, transmissions, bodyTypes } = derive(list));
};

export const stockUpdatedLabel = () =>
  isLive && lastSynced
    ? `updated ${new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Europe/London" }).format(new Date(lastSynced))}`
    : `snapshot from ${SNAPSHOT_DATE}`;

export const modelOptions = (make) =>
  vehicles
    .filter((v) => !make || v.make === make)
    .reduce((acc, v) => (acc.some((o) => o.value === v.model) ? acc : [...acc, { value: v.model, make: v.make, label: make ? v.model : `${v.make} ${v.model}` }]), [])
    .sort((a, b) => a.label.localeCompare(b.label));
export const makeOfModel = (model) => vehicles.find((v) => v.model === model)?.make || "";

export const PRICE_STEPS = [2000, 3000, 4000, 5000, 6000, 7000, 8000, 10000];
export const SORTS = [
  { value: "newest", label: "Newest listed" },
  { value: "price-asc", label: "Price: low to high" },
  { value: "price-desc", label: "Price: high to low" },
  { value: "mileage-asc", label: "Mileage: lowest first" },
];

export const EMPTY_FILTERS = { make: "", model: "", minPrice: 0, maxPrice: 0, fuel: [], transmission: [], body: [], sort: "newest" };

export const filterVehicles = (list, f) =>
  list.filter(
    (v) =>
      (!f.make || v.make === f.make) &&
      (!f.model || v.model === f.model) &&
      (!f.minPrice || v.price >= f.minPrice) &&
      (!f.maxPrice || v.price <= f.maxPrice) &&
      (!f.fuel.length || f.fuel.includes(v.fuel)) &&
      (!f.transmission.length || f.transmission.includes(v.transmission)) &&
      (!f.body.length || f.body.includes(v.bodyType)),
  );

const SORTERS = {
  newest: (a, b) => b.id.localeCompare(a.id),
  "price-asc": (a, b) => a.price - b.price,
  "price-desc": (a, b) => b.price - a.price,
  "mileage-asc": (a, b) => a.mileage - b.mileage,
};
export const sortVehicles = (list, sort) => [...list].sort(SORTERS[sort] || SORTERS.newest);

export const toParams = (f) => {
  const p = {};
  if (f.make) p.make = f.make;
  if (f.model) p.model = f.model;
  if (f.minPrice) p.minPrice = String(f.minPrice);
  if (f.maxPrice) p.maxPrice = String(f.maxPrice);
  ["fuel", "transmission", "body"].forEach((k) => {
    if (f[k]?.length) p[k] = f[k].join(",");
  });
  if (f.sort && f.sort !== "newest") p.sort = f.sort;
  return p;
};

export const latestArrivals = (n = 6) => sortVehicles(vehicles.filter((v) => v.images.length), "newest").slice(0, n);

export const relatedTo = (v, n = 3) =>
  vehicles
    .filter((o) => o.id !== v.id && o.images.length)
    .map((o) => ({ o, score: Math.abs(o.price - v.price) / 1000 + (o.bodyType === v.bodyType ? 0 : 2.5) }))
    .sort((a, b) => a.score - b.score)
    .slice(0, n)
    .map((x) => x.o);

export const badgesFor = (v) =>
  [v.ulez && "ULEZ compliant", v.serviceHistory === "Full" && "Full service history"].filter(Boolean);

export const countBy = (key, value) => vehicles.filter((v) => v[key] === value).length;
